<!-- NOTE TO DEVS: This component is the beginning of a "dynamic fields tree v2." It's being worked amidst production work, so it will likely be in a WIP state until the next cool-down sprint.
      It is currently only used in the Process Flow Builder, so some of the logic in dependant components is more coupled than it should eventually be. -->

<template>
  <div class="dynamic-field-menu">
    <slot name="heading"></slot>
    <p v-if="max">{{currentValue.length}}/10 selected</p>
    <input v-if="!unsearchable" v-model="q" type="text" placeholder="Search">
    <div v-if="showDynamicMessage">{{dynamicMessage}}</div>

    <template v-if="!disabledMessage">
      <scroll-area class="dynamic-box">
        <template v-for="(handler, ind) in getAllHandlers">
          <component
              v-for="component in handler.COMPONENTS"
              :key="ind"
              :is="component"
              :handler="handler"
              :q="q"
              :parent="parent"
              :restrict-results-to-parent-context="restrictResultsToParentContext"
              @select="select"
              :from-model-view="fromModelView"
          ></component>
        </template>
      </scroll-area>
    </template>
    <template v-else>
      <div class="alert warning border justify-content-center">
        <icon glyph="exclamation-circle"/>&nbsp;<strong>{{ disabledMessage }}</strong>
      </div>
    </template>

    <div v-if="modeIs('checkbox')" class="cta">
      <button @click="cancel" class="btn block red outline">Cancel</button>
      <button @click="confirm" class="btn block green">Done</button>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import {StringHelper} from "../../helpers/stringHelper";
  import {InstanceHelper} from "../../helpers/instanceHelper";
  import DynamicFieldsHandler from "../../models/dynamicFields/handlers/dynamicFieldsHandler";
  import {FormElementType} from "../../enums/formElementType";
  import {IDynamicDataConfig} from "~/interfaces/dynamicFieldsInterfaces";
  import componentsRegisterMixin from "~/components/dynamicFieldsTrees/mixins/componentsRegisterMixin";
  import selectedMixin from "../../../formability/processFlowBuilder/mixins/selectedMixin";

  export default Vue.extend({
    mixins: [componentsRegisterMixin, selectedMixin],
    props: {
      parent: {
        type: Object,
        default: null
      },
      restrictResultsToParentContext: {
        type: Boolean,
        default: false
      },
      mode: {
        type: String,
        default: 'select'
      },
      value: {
        default: null
      },
      max: { // used for checkbox mode
        type: Number,
        default: null
      },
      fieldsHandler: {
        type: Object as () => DynamicFieldsHandler,
      },
      inDocumentBuilder: {
        type: Boolean,
        default: false
      },
      unsearchable: {
        type: Boolean,
        default: false,
      },
      disabledMessage: {
        type: String,
        default: null
      },
      fromModelView: {
        type: Boolean,
        default: false,
      }
    },
    data: function () {
      return {
        q: '',
        hasSearched: false,
        currentValue: InstanceHelper.clone(this.value),
        showDynamicMessage: false,
        dynamicMessage: '',
        FormElementType
      };
    },
    computed: {
      getAllHandlers(): DynamicFieldsHandler[] {
        return this.fieldsHandler.getAll();
      },
      lowerQ(): string {
        return StringHelper.escape(this.q.toLowerCase());
      },
      maxReached(): boolean {
        if (this.mode === 'checkbox') {
          return this.currentValue.length >= this.max;
        } else {
          return false;
        }
      },
    },
    methods: {
      modeIs(mode: string): boolean {
        return this.mode === mode;
      },
      select(config: IDynamicDataConfig): void {
        this.$emit('select', config);
      },
      shouldShow(area: string): boolean {
        return this.fieldsHandler.shouldShow(area, this.lowerQ);
      },
      cancel(event): void {
        this.$emit('cancel', event);
      },
      confirm(): void {
        this.$emit('confirm', this.currentValue);
      },
      canEdit(item): boolean {
        if (this.mode === 'checkbox') {
          return !this.maxReached || this.currentValue.includes(item);
        } else {
          return true;
        }
      },
      updateDynamicMessage(handler = null): void {
        // must manually call this method - unsure why Vue is not being reactive to changes to these fields
        if (handler) {
          this.dynamicMessage = handler.dynamicMessage;
          this.showDynamicMessage = handler.showDynamicMessage;
        } else {
          this.dynamicMessage = this.fieldsHandler.dynamicMessage;
          this.showDynamicMessage = this.fieldsHandler.showDynamicMessage;
        }
      },
    },
    watch: {
      q: function (newVal, oldVal) {
        this.hasSearched = true;
      },
      currentValue: function (newVal, oldVal) {
        if (this.mode === 'checkbox') {
          this.$emit('input', newVal);
        }
      }
    }
  });
</script>
